import React, { useState } from "react";
import { Form, Button, Card, Table, Badge, Row, Col } from "react-bootstrap";
import axios from "axios";
import { useLocation } from "react-router-dom";

const TransactionForm = () => {
  const location = useLocation();
  const { reservation, API_BASE, token, branch } = location.state;
  
  const [txnType, setTxnType] = useState("debit"); // debit = Payment, credit = Charge
  const [loading, setLoading] = useState(false);

  // ✅ Use existing billId or create one
  const billId = reservation.billId || `BILL-${Date.now()}`;

  const [payments, setPayments] = useState([
    { id: Date.now(), amount: "", paymentMode: "cash", corporateId: "", referenceId: "", description: "" }
  ]);
  
  const [charges, setCharges] = useState([
    { id: Date.now(), category: "room_charge", amount: "", description: "" }
  ]);

  /* ---------------- HANDLERS ---------------- */

  const addRow = () => {
    setPayments([...payments, { id: Date.now() + Math.random(), amount: "", paymentMode: "cash", corporateId: "", referenceId: "", description: "" }]);
  };

  const addChargeRow = () => {
    setCharges([...charges, { id: Date.now() + Math.random(), category: "room_charge", amount: "", description: "" }]);
  };

  const handlePaymentChange = (id, field, value) => {
    setPayments(payments.map(p => p.id === id ? { ...p, [field]: value } : p));
  };

  const handleChargeChange = (id, field, value) => {
    setCharges(charges.map(c => c.id === id ? { ...c, [field]: value } : c));
  };

  /* ---------------- SUBMIT ---------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      if (txnType === "debit") {
        const validPayments = payments.filter(p => p.amount && Number(p.amount) > 0);
        if (validPayments.length === 0) return alert("Enter at least one valid payment");

        await axios.post(`${API_BASE}transactions/${branch}`, {
          billId,
          bookingId: reservation._id,
          customerId: reservation.customerId,
          type: "debit",
          payments: validPayments.map(p => ({
            amount: Number(p.amount),
            paymentMode: p.paymentMode,
            corporateId: p.paymentMode === "corporate" ? p.corporateId : null,
            referenceId: p.referenceId || "",
            description: p.description || "Payment received" // ✅ Included description
          })),
        }, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        const validCharges = charges.filter(c => c.amount && Number(c.amount) > 0);
        if (validCharges.length === 0) return alert("Enter at least one charge");

        await axios.post(`${API_BASE}transactions/${branch}`, {
          billId,
          bookingId: reservation._id,
          customerId: reservation.customerId,
          type: "credit",
          charges: validCharges.map(c => ({
            category: c.category,
            amount: Number(c.amount),
            description: c.description || "",
          })),
        }, { headers: { Authorization: `Bearer ${token}` } });
      }

      alert("Transaction saved successfully");
      setPayments([{ id: Date.now(), amount: "", paymentMode: "cash", corporateId: "", referenceId: "", description: "" }]);
      setCharges([{ id: Date.now(), category: "room_charge", amount: "", description: "" }]);
    } catch (err) {
      console.error(err);
      alert("Error saving transaction");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="shadow-sm border-0">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">💳 Transactions</h5>
          <Badge bg={txnType === "debit" ? "success" : "warning"}>
            {txnType === "debit" ? "Payment" : "Charge"}
          </Badge>
        </div>

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-4">
            <Form.Label className="fw-bold">Transaction Type</Form.Label>
            <Form.Select value={txnType} onChange={(e) => setTxnType(e.target.value)}>
              <option value="debit">Payment (Customer Paid)</option>
              <option value="credit">Charge (Added to Bill)</option>
            </Form.Select>
          </Form.Group>

          {/* PAYMENT VIEW */}
          {txnType === "debit" && (
            <div className="mb-3">
              <Table responsive bordered hover size="sm">
                <thead className="bg-light">
                  <tr>
                    <th style={{ width: '150px' }}>Amount</th>
                    <th style={{ width: '150px' }}>Mode</th>
                    <th>Ref / Corp ID</th>
                    <th>Note / Description</th>
                    <th style={{ width: '50px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => (
                    <tr key={p.id}>
                      <td>
                        <Form.Control type="number" placeholder="0.00" value={p.amount} onChange={(e) => handlePaymentChange(p.id, "amount", e.target.value)} />
                      </td>
                      <td>
                        <Form.Select value={p.paymentMode} onChange={(e) => handlePaymentChange(p.id, "paymentMode", e.target.value)}>
                          <option value="cash">Cash</option>
                          <option value="card">Card</option>
                          <option value="upi">UPI</option>
                          <option value="corporate">Corporate</option>
                        </Form.Select>
                      </td>
                      <td>
                        <Form.Control 
                          placeholder={p.paymentMode === "corporate" ? "Enter Corp ID" : "Ref / Txn ID"} 
                          value={p.paymentMode === "corporate" ? p.corporateId : p.referenceId} 
                          onChange={(e) => handlePaymentChange(p.id, p.paymentMode === "corporate" ? "corporateId" : "referenceId", e.target.value)} 
                        />
                      </td>
                      <td>
                        <Form.Control 
                          placeholder="e.g. Advance, GPay" 
                          value={p.description} 
                          onChange={(e) => handlePaymentChange(p.id, "description", e.target.value)} 
                        />
                      </td>
                      <td className="text-center align-middle">
                        {payments.length > 1 && (
                          <Button variant="link" className="text-danger p-0" onClick={() => setPayments(payments.filter(x => x.id !== p.id))}>✕</Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <Button variant="outline-primary" size="sm" onClick={addRow}>+ Add Split Payment</Button>
            </div>
          )}

          {/* CHARGE VIEW (unchanged structure, just ensured consistency) */}
          {txnType === "credit" && (
            <div className="mb-3">
              <Table responsive bordered hover size="sm">
                <thead className="bg-light">
                  <tr>
                    <th>Category</th>
                    <th style={{ width: '150px' }}>Amount</th>
                    <th>Description</th>
                    <th style={{ width: '50px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {charges.map((c) => (
                    <tr key={c.id}>
                      <td>
                        <Form.Select value={c.category} onChange={(e) => handleChargeChange(c.id, "category", e.target.value)}>
                          <option value="room_charge">Room</option>
                          <option value="food_charge">Food</option>
                          <option value="laundry">Laundry</option>
                          <option value="other">Other</option>
                        </Form.Select>
                      </td>
                      <td>
                        <Form.Control type="number" placeholder="0.00" value={c.amount} onChange={(e) => handleChargeChange(c.id, "amount", e.target.value)} />
                      </td>
                      <td>
                        <Form.Control placeholder="Item details..." value={c.description} onChange={(e) => handleChargeChange(c.id, "description", e.target.value)} />
                      </td>
                      <td className="text-center align-middle">
                        {charges.length > 1 && (
                          <Button variant="link" className="text-danger p-0" onClick={() => setCharges(charges.filter(x => x.id !== c.id))}>✕</Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <Button variant="outline-primary" size="sm" onClick={addChargeRow}>+ Add Charge Item</Button>
            </div>
          )}

          <hr />
          <div className="d-flex justify-content-between align-items-center mt-3">
            <h5 className="mb-0">
              Total Amount: <span className="text-primary">₹ {txnType === "debit" ? payments.reduce((s, p) => s + Number(p.amount || 0), 0) : charges.reduce((s, c) => s + Number(c.amount || 0), 0)}</span>
            </h5>
            <Button type="submit" variant="success" size="lg" disabled={loading}>
              {loading ? "Processing..." : "Submit Transaction"}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default TransactionForm;